import {ModalComponentProps, Block, Button, Card, Dialog, DialogBody, DialogFooter, Text} from '@mc/ui-v4';

import React, {useEffect, useRef} from 'react';
import {useQuery} from 'react-query';
import {JsonImportApi, JsonImportProgressVO, JsonImportResultVO, JsonImportState} from '../../../api';
import {JsonImportProgressContent} from './JsonImportProgressContent';

type Props = ModalComponentProps & {
    title: string;
    initialProgress: JsonImportProgressVO;
    onFinished: (result?: JsonImportResultVO) => void | Promise<void>;
    onFailed: (message?: string) => void;
    onLayerAppear?: (layerId: number) => void;
};

const POLL_INTERVAL_MS = 1000;

export const JsonImportProgressModal: React.FC<Props> = ({onClose, title, initialProgress, onFinished, onFailed, onLayerAppear}) => {
    const jobId = initialProgress.jobId;
    const settledRef = useRef(false);
    const layerAppearedRef = useRef(false);
    const settle = (action: () => void | Promise<void>) => {
        if (settledRef.current) {
            return;
        }
        settledRef.current = true;
        Promise.resolve(action()).finally(onClose);
    };

    const {data, refetch} = useQuery<JsonImportProgressVO>(
        ['jsonImportProgress', jobId],
        async () => (await JsonImportApi.getJsonImportProgress(jobId!)).data,
        {
            enabled: !!jobId,
            initialData: initialProgress,
            refetchOnWindowFocus: false,
            onError: () => settle(() => onFailed('Nepodařilo se zjistit průběh importu.')),
        },
    );

    const progress = data ?? initialProgress;
    const running = progress.state === JsonImportState.RUNNING;

    useEffect(() => {
        if (!running) {
            return;
        }
        const id = setInterval(() => {
            void refetch();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [running, refetch]);

    useEffect(() => {
        if (!layerAppearedRef.current && progress.layerId) {
            layerAppearedRef.current = true;
            onLayerAppear?.(progress.layerId);
        }
    }, [progress.layerId, onLayerAppear]);

    useEffect(() => {
        if (progress.state === JsonImportState.DONE) {
            settle(() => onFinished(progress.result));
        } else if (progress.state === JsonImportState.ERROR) {
            settle(() => onFailed(progress.errorMessage));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [progress.state]);

    return (
        <Dialog onClose={onClose} title={title}>
            <DialogBody>
                <Card>
                    <Block>
                        <JsonImportProgressContent progress={progress} />
                        <div className="mt-3">
                            <Text intent={'secondary'}>
                                Import běží na pozadí. Okno můžete zavřít, import bude pokračovat a jeho průběh uvidíte i v detailu vrstvy.
                            </Text>
                        </div>
                    </Block>
                </Card>
            </DialogBody>
            <DialogFooter>
                <Button style={'link'} onClick={onClose}>
                    Zavřít
                </Button>
            </DialogFooter>
        </Dialog>
    );
};
